import { InkClustersPart } from "../../shared/types/PromptPart";
import { AgentRequest } from "../../shared/types/AgentRequest";
import { AgentHelpers } from "../AgentHelpers";
import { PromptPartUtil, registerPromptPartUtil } from "./PromptPartUtil";

/**
 * Report the student's handwriting as named groups of pen strokes.
 *
 * `blurryShapes` reports every tldraw shape separately, and a single line of
 * handwriting is 5-20 `draw` shapes. That leaves the agent a row of opaque,
 * textless stroke ids with nothing to say which of them are one piece of writing
 * — so "underline the student's answer" has no ref to target. This part carries
 * the grouping (and a stable name per group) so the gateway can model one
 * addressable element per unit of writing.
 *
 * The grouping itself lives in `computeStudentInkClusters`, shared with the
 * screenshot badges so the names in the prompt and the names drawn on the image
 * are always the same names.
 */
export const InkClustersPartUtil = registerPromptPartUtil(
  class InkClustersPartUtil extends PromptPartUtil<InkClustersPart> {
    static override type = "inkClusters" as const;

    override getPart(
      request: AgentRequest,
      helpers: AgentHelpers,
    ): InkClustersPart {
      const clusters = helpers.getStudentInkClusters(request.bounds);

      return {
        type: "inkClusters",
        clusters: clusters.map(({ label, bounds, strokeIds, changed }) => {
          // Rounded like blurryShapes: the gateway folds both into one element.
          const box = helpers.roundBox(bounds);
          return {
            label,
            x: box.x,
            y: box.y,
            w: box.w,
            h: box.h,
            strokeIds,
            // Feeds the digest's `new` tag: the gateway cannot see an erasure,
            // and this keeps tag, badge and crop stamp one boolean.
            changed,
          };
        }),
      };
    }
  },
);
